import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, List, ArrowRight, Building2, Flame } from 'lucide-react';
import { listOrganizations } from '../services/organizationService';
import { listTenants } from '../services/tenantService';
import { listSystemUsers } from '../services/systemUserService';
import { listLookupTypes } from '../services/lookupService';
import InspectorPresencePanel from '../components/InspectorPresencePanel';

interface Stats {
  organizations: number;
  tenants: number;
  users: number;
  lookupTypes: number;
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats>({ organizations: 0, tenants: 0, users: 0, lookupTypes: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      listOrganizations().catch(() => []),
      listTenants().catch(() => []),
      listSystemUsers().catch(() => []),
      listLookupTypes().catch(() => []),
    ]).then(([orgs, tenants, users, types]) => {
      setStats({
        organizations: orgs.length,
        tenants: tenants.length,
        users: users.length,
        lookupTypes: types.length,
      });
      setLoading(false);
    });
  }, []);

  const cards = [
    { label: 'Organizations', value: stats.organizations, icon: Building2, to: '/organizations', color: 'bg-blue-500' },
    { label: 'Departments', value: stats.tenants, icon: Flame, to: '/tenants', color: 'bg-orange-500' },
    { label: 'Users', value: stats.users, icon: Users, to: '/users', color: 'bg-green-500' },
    { label: 'Lookup Types', value: stats.lookupTypes, icon: List, to: '/lookups', color: 'bg-purple-500' },
  ];

  return (
    <div className="theme-grid p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">FireSquirrel Platform Overview</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {cards.map(({ label, value, icon: Icon, to, color }) => (
          <Link
            key={label}
            to={to}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${color} p-3 rounded-lg`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {loading ? '—' : value}
            </p>
            <p className="text-sm text-gray-500 mt-1">{label}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/organizations"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
            >
              <Building2 className="h-4 w-4" /> Create Organization
            </Link>
            <Link
              to="/tenants"
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-100 transition-colors"
            >
              <Flame className="h-4 w-4" /> Create Department
            </Link>
            <Link
              to="/users"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
            >
              <Users className="h-4 w-4" /> Invite User
            </Link>
          </div>
        </div>

        <InspectorPresencePanel />
      </div>
    </div>
  );
}
